import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, FileText } from "lucide-react";

/**
 * Download Brochure — dark rounded card with optional custom SVG blueprint
 * schematic in the background + primary orange download button.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Documentation")
 *  - heading (ReactNode)  — spans allowed, will render as "Download Product [orange word]"
 *  - subtitle
 *  - buttonLabel (default "Download PDF Brochure")
 *  - onButtonClick?: () => void
 *  - blueprintSvg?: ReactNode  (custom SVG rendered at 14% opacity on the right)
 */
export default function DownloadBrochure({
  testId,
  eyebrow = "Documentation",
  heading,
  subtitle,
  buttonLabel = "Download PDF Brochure",
  onButtonClick,
  blueprintSvg,
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-cream-light py-20 md:py-24 overflow-hidden"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-3xl bg-liftile-matte-black text-liftile-cream shadow-[0_25px_70px_-20px_rgba(0,0,0,0.35)]"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "52px 52px",
            }}
          />
          {blueprintSvg && blueprintSvg}

          <div className="relative grid md:grid-cols-[1.2fr_auto] gap-8 items-center p-8 sm:p-10 md:p-14 lg:p-16">
            <div>
              <div className="heading-eyebrow mb-4">{eyebrow}</div>
              <h2
                data-testid={`${testId}-heading`}
                className="font-display uppercase text-liftile-cream tracking-tight text-3xl sm:text-4xl md:text-[2.75rem] leading-[1.05]"
              >
                {heading}
              </h2>
              {subtitle && (
                <p className="font-body text-liftile-cream/70 mt-5 max-w-xl text-base md:text-lg leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>

            <a
              href="/liftile-brochure.pdf"
              download="LIFTILE-Product-Catalogue.pdf"
              onClick={onButtonClick}
              data-testid={`${testId}-btn`}
              className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-[18px] rounded-xl hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)] transition-all duration-400 self-start md:self-center"
            >
              <FileText className="w-4 h-4" />
              {buttonLabel}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
